/** @jsxImportSource @emotion/react */

import Modal from "react-modal";
import UI from "@mile-meeting/mile-ui";
import { getDay, getKorDate, getTimeString } from "utils/date";
import { TRestrictRequest } from "apis/meetings/block-request";
import { useEffect, useState } from "react";
import { MeetingRoomHorizontalNonModal } from "uis/non-modal";
import { getMeetingRoomList, TMeetingRoom } from "apis/meetings/meeting-room";

const modalStyle = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    border: "none",
    background: "transparent",
    transform: "translate(-50%, -50%)",
    width: 480,
    overflow: "visible",
  },
  overlay: {
    backgroundColor: "var(--component-color-material-dimmer)",
    zIndex: 1000,
  },
};
Modal.setAppElement("#root");

type Props = {
  modalIsOpen: boolean;
  closeModal: () => void;
  onCancel?: () => void;
  onApproval?: () => Promise<void>;
  onReject?: () => void;
  data?: TRestrictRequest;
};

const RequestDetailModal = ({ modalIsOpen, closeModal, onCancel, onApproval, onReject, data }: Props) => {
  const [approvalBtnLoading, setApprovalBtnLoading] = useState<boolean>(false);
  const [showDetailMeetingRoomInfo, setShowDetailMeetingRoomInfo] = useState<boolean>(false);
  const [meetingRoomDetailInfo, setMeetingRoomDetailInfo] = useState<TMeetingRoom>();

  useEffect(() => {
    if (modalIsOpen) {
      getMeetingRoomList(1, [], [], 100).then((meetingRoomList) => {
        const detailMeetingRoom = meetingRoomList.data?.meetingRoomList.find(
          (item) => item.id === data?.meetingRoom.id
        );
        setMeetingRoomDetailInfo(detailMeetingRoom);
      });
    }
  }, [modalIsOpen]);

  if (!data) return null;
  return (
    <Modal isOpen={modalIsOpen} onRequestClose={closeModal} style={modalStyle}>
      <div
        style={{
          background: "white",
          padding: 40,
          borderRadius: 16,
          gap: 20,
          display: "flex",
          flexDirection: "column",
          position: "relative",
        }}
        onMouseDown={(e) => {
          e.stopPropagation();
        }}
      >
        <div
          className="closeButton"
          style={{
            position: "absolute",
            top: 20,
            right: 20,
          }}
        >
          <UI.IconButton.Normal icon="x" size={20} color="var(--alias-color-label-normal)" onClick={closeModal} />
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            borderBottom: "1px solid var(--alias-color-line-normal)",
            paddingBottom: 20,
            alignItems: "flex-start",
            gap: 8,
          }}
        >
          {data.status === "PENDING" && <UI.Tag label="승인 대기" size="small" color="gray" />}
          {data.status === "APPROVED" && <UI.Tag label="승인" size="small" color="skyblue" />}
          {data.status === "REJECTED" && <UI.Tag label="반려" size="small" color="pink" />}
          <UI.Typography type="title03" fontStyle="bold">
            예약 제한 요청 상세 정보
          </UI.Typography>
          {data.status === "REJECTED" && (
            <div
              style={{
                padding: "16px 20px",
                borderRadius: 12,
                border: "1px solid var(--global-color-pink-70)",
                alignSelf: "stretch",
                background: "var(--alias-color-accent-light-pink)",
              }}
            >
              <UI.Typography type="label01" fontStyle="regular">
                {data.resultReason}
              </UI.Typography>
            </div>
          )}
          {data.status === "APPROVED" && (
            <div
              style={{
                padding: "16px 20px",
                borderRadius: 12,
                border: "1px solid var(--alias-color-accent-normal-skyblue)",
                alignSelf: "stretch",
                background: "var(--alias-color-accent-light-skyblue)",
              }}
            >
              <UI.Typography type="label01" fontStyle="regular">
                {data.resultReason}
              </UI.Typography>
            </div>
          )}
        </div>
        <div style={{ gap: 40, display: "flex", flexDirection: "column" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }} className="meetingRoom">
              <div style={{ display: "flex", gap: 4, alignItems: "center" }} className="label">
                <UI.Icon name="meetingRoom2" size={16} color="var(--alias-color-label-assistive)" />
                <UI.Typography color="var(--alias-color-label-alternative)" type="label01" fontStyle="bold">
                  회의실
                </UI.Typography>
              </div>
              <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                <UI.Typography type="body01 normal" fontStyle="regular">
                  {data.meetingRoom.name}
                </UI.Typography>
                <div
                  style={{ position: "relative", display: "flex", alignItems: "center" }}
                  onMouseEnter={() => setShowDetailMeetingRoomInfo(true)}
                  onMouseLeave={() => setShowDetailMeetingRoomInfo(false)}
                >
                  <UI.Icon name="info" size={20} color="var(--alias-color-label-assistive)" />
                  {meetingRoomDetailInfo && (
                    <MeetingRoomHorizontalNonModal
                      meetingRoom={meetingRoomDetailInfo}
                      open={showDetailMeetingRoomInfo}
                      position={{
                        left: 0,
                        top: 24,
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }} className="meetingTitle">
              <div style={{ display: "flex", gap: 4, alignItems: "center" }} className="label">
                <UI.Icon name="book" size={16} color="var(--alias-color-label-assistive)" />
                <UI.Typography color="var(--alias-color-label-alternative)" type="label01" fontStyle="bold">
                  예약 제목
                </UI.Typography>
              </div>
              <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                <UI.Typography type="body01 normal" fontStyle="regular">
                  {data.title ?? ""}
                </UI.Typography>
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }} className="time">
              <div style={{ display: "flex", gap: 4, alignItems: "center" }} className="label">
                <UI.Icon name="calendar" size={16} color="var(--alias-color-label-assistive)" />
                <UI.Typography color="var(--alias-color-label-alternative)" type="label01" fontStyle="bold">
                  예약 제한 기간
                </UI.Typography>
              </div>
              <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                <UI.Typography type="body01 normal" fontStyle="regular">
                  {getKorDate(data.startDateTime.toDateString()).replaceAll(".", ". ")} (
                  {getDay(data.startDateTime.toDateString())}) {getTimeString(data.startDateTime.toString())} -{" "}
                  {getKorDate(data.endDateTime.toDateString()).replaceAll(".", ". ")} (
                  {getDay(data.endDateTime.toDateString())}) {getTimeString(data.endDateTime.toString())}
                </UI.Typography>
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }} className="request_host">
              <div style={{ display: "flex", gap: 4, alignItems: "center" }} className="label">
                <UI.Icon name="person" size={16} color="var(--alias-color-label-assistive)" />
                <UI.Typography color="var(--alias-color-label-alternative)" type="label01" fontStyle="bold">
                  요청자
                </UI.Typography>
              </div>
              <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                <UI.Typography type="body01 normal" fontStyle="regular">
                  {data.requestBy?.nickname} ({data.requestBy?.email})
                </UI.Typography>
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }} className="time">
              <div style={{ display: "flex", gap: 4, alignItems: "center" }} className="label">
                <UI.Icon name="notes" size={16} color="var(--alias-color-label-assistive)" />
                <UI.Typography color="var(--alias-color-label-alternative)" type="label01" fontStyle="bold">
                  요청 사유
                </UI.Typography>
              </div>
              <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                <UI.Typography type="body01 normal" fontStyle="regular">
                  {data.requestReason}
                </UI.Typography>
              </div>
            </div>
          </div>
          {data.status === "PENDING" && onCancel && (
            <div style={{ display: "flex", flex: 1, justifyContent: "flex-end" }}>
              <UI.Button title="취소하기" type="solid" primary={false} onClick={onCancel} />
            </div>
          )}
          {data.status === "PENDING" && onApproval && onReject && (
            <div style={{ display: "flex", flex: 1, justifyContent: "flex-end", gap: 8 }}>
              <UI.Button
                title="승인하기"
                type="solid"
                primary={false}
                disabled={approvalBtnLoading || data.endDateTime < new Date()}
                onClick={async () => {
                  if (approvalBtnLoading) return;
                  if (data.endDateTime < new Date()) return;
                  setApprovalBtnLoading(true);
                  await onApproval();
                  setApprovalBtnLoading(false);
                }}
              />
              <UI.Button title="반려하기" type="outlined" primary onClick={onReject} />
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default RequestDetailModal;
